import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { currentRoute, navigate } from '../lib/router';
import { primaryNavFor, showMyDesk, driveLabelFor, type Role } from '../lib/nav';
import { refetchResource, state, useResource } from '../lib/store';
import { signOut } from '../lib/auth';
import { session } from '../lib/session';
import { authedGet } from '../lib/apiClient';

// Class names match boardCss_'s .nav a/.on/.later verbatim (the real app's
// nav row: uppercase 10.5px, 2.4px letter-spacing, weight 700, grey, active
// item dark with a 2px purple underline).
function NavLink(
  { path, label, muted, lowerPrefix }: { path: string; label: string; muted?: boolean; lowerPrefix?: string },
): JSX.Element {
  const active = currentRoute.value.path === path;
  // The row is set in capitals by CSS; a product name's opening letters
  // (nvRecruiter's "nv") sit in their own span that opts out, so the text
  // itself is still the plain label (nav.ts `lowerPrefix`).
  const text = lowerPrefix && label.startsWith(lowerPrefix)
    ? <><span class="nav-lower">{lowerPrefix}</span>{label.slice(lowerPrefix.length)}</>
    : label;
  if (muted) {
    // A muted item is a plain non-interactive span, not a disabled-looking
    // link that still navigates.
    return <span class="later">{text}</span>;
  }
  return (
    <a
      class={active ? 'on' : undefined}
      href={path}
      aria-current={active ? 'page' : undefined}
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        navigate(path);
      }}
    >
      {text}
    </a>
  );
}

/**
 * Gmail, for the person actually signed in — NOT whoever the browser happens to
 * be defaulting to.
 *
 * A bare `https://mail.google.com/` opens the browser's DEFAULT Google account.
 * The owner found this immediately, signed in as manager0499: the Gmail icon
 * took him to tomer@nvmanager.com's inbox. On a shared clinic computer that is
 * worse than a broken link — it is one account's mail presented to another
 * person as if it were theirs.
 *
 * `authuser=<email>` tells Google which of the signed-in accounts to open. If
 * that account is not signed in to the browser, Google shows its own account
 * chooser, which is the correct and safe outcome; it never silently falls back
 * to a different mailbox.
 */
export function gmailUrlFor(email: string | null): string {
  if (!email) return 'https://mail.google.com/';
  return `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(email)}`;
}

/**
 * Google Chat, for the person actually signed in — same authuser trap as Gmail.
 */
export function chatUrlFor(email: string | null): string {
  if (!email) return 'https://chat.google.com/';
  return `https://chat.google.com/?authuser=${encodeURIComponent(email)}`;
}

export function formatUnread(n: number | null | undefined): string | null {
  if (n == null || n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}

/**
 * Google Calendar, for the person actually signed in (owner, 2026-08-17: "add
 * the calendar logo after the Gmail logo before the idea bulb").
 *
 * Same discipline and the same reason as `gmailUrlFor`: a bare
 * `calendar.google.com` opens whichever Google account the BROWSER defaults to,
 * which on a shared clinic machine is somebody else's diary.
 */
export function calendarUrlFor(email: string | null): string {
  if (!email) return 'https://calendar.google.com/calendar/r';
  return `https://calendar.google.com/calendar/r?authuser=${encodeURIComponent(email)}`;
}

/**
 * THE KEEP MARK DOES NOT OPEN KEEP.
 *
 * The owner asked for a Keep button that would gather what managers write and
 * mail it to him. Keep cannot do that in any way worth trusting — no ordinary
 * API, and a note living there carries no clinic, no author this system knows
 * and no date it can group by. Told that, he chose the better thing himself:
 * "just use the Google Keep Logo but have it open a note entry form that they
 * can keep adding notes that then get logged and sent to me" — for "ideas they
 * want me to automate", with "a title and note".
 *
 * So the logo is the doorway he recognises and everything behind it is ours:
 * /ideas, backed by `automation_idea` (migrations 037-039). Left as a function
 * so the destination is stated in one place rather than inlined in the markup.
 */
export function keepUrlFor(_email?: string | null): string {
  return '/ideas';
}

/**
 * Rackspace Email webmail. One URL for everyone — this is not a Google
 * account, so there is no authuser trap and nothing to look up per person.
 * The mark sits immediately after the ideas bulb and opens in a new tab.
 */
export function rackspaceUrlFor(): string {
  return 'https://webmail.emailsrvr.com/wmidentity/account/login';
}

/**
 * The same problem, and the same fix, for the Drive link.
 *
 * The owner, signed into the app as manager0499, clicked the Drive mark and
 * landed in the right folder — but *as himself*, "in my main google Drive i can
 * access everything". A plain `drive.google.com/...` link opens under whichever
 * Google account the BROWSER is defaulting to, which on his machine is the
 * owner account. The folder was correct; the identity was not.
 *
 * `authuser=<email>` names the account the link should open under. Google
 * switches to it, or shows its account chooser if that account is not signed in
 * to the browser.
 *
 * To be exact about what this does and does not do, because it would be easy to
 * misread as a security control: **it does not restrict anything.** If a person
 * is signed into Chrome as the owner, their Google account still sees whatever
 * it always saw. This makes the app's links open as the app's user, which is
 * the difference between a manager seeing their own drive and a manager sitting
 * in front of the owner's. Access itself is Drive membership, set in Drive.
 */
export function withGoogleAccount(url: string, email: string | null): string {
  if (!email) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}authuser=${encodeURIComponent(email)}`;
}

interface DriveShortcut {
  configured: boolean;
  url?: string;
  label?: string;
  reason?: string;
}

const INBOX_BADGES_KEY = 'inbox-badges';

export function Nav({ role }: { role: Role }): JSX.Element {
  const items = primaryNavFor(role);
  const myDesk = showMyDesk(role);
  const label = driveLabelFor(role);
  const email = session.value.me?.email ?? null;
  const badges = useResource<{ mail?: number | null; chat?: number | null }>(
    email ? INBOX_BADGES_KEY : null,
    () => authedGet('/api/v1/inbox-badges'),
  );
  useEffect(() => {
    if (!email) return undefined;
    const id = setInterval(() => {
      void refetchResource(INBOX_BADGES_KEY, () => authedGet('/api/v1/inbox-badges'));
    }, 120000);
    return () => clearInterval(id);
  }, [email]);
  const mailLabel = badges.status === 'ready' ? formatUnread(badges.data.mail) : null;
  const chatLabel = badges.status === 'ready' ? formatUnread(badges.data.chat) : null;

  // The Drive folder this person's item opens (D-36), read from the snapshot —
  // the Nav must not fetch, because it renders on every navigation and INV-1
  // budgets that at zero network calls. A person with no folder set up KEEPS
  // the item, disabled and saying why: a nav item that silently disappears
  // reads as a permissions problem (INV-9).
  const drive = (state.value.driveShortcut as Record<string, DriveShortcut> | undefined)?.me ?? null;
  return (
    <nav class="nav" aria-label="Primary">
      {items.map((item) => (
        <NavLink key={item.key} path={item.path} label={item.label} muted={item.muted} lowerPrefix={item.lowerPrefix} />
      ))}
      {/* The marks sit at the RIGHT end of the row (owner, 2026-09-15: "move
          the icons to the right side"). `.nav .sep` grows to take the slack;
          the words stay pinned left under the wordmark (2026-08-16). */}
      <span class="sep" aria-hidden="true" data-testid="nav-sep" />
      {/* The Drive folder, as the Google Drive LOGO immediately to the right of
          Company (owner, 2026-08-15: "instead of the T drive, C drive, my drive
          — a logo of Google Drive to the right of the Company menu item, a
          clickable logo"). The words are gone; the mark is the control.
          The accessible name still says which drive it is, so a screen reader
          and a hover tooltip both name it — an icon with no name is a control
          nobody can identify. The separator went with the words: there is no
          longer a text item to set apart. */}
      {myDesk && (
        <>
          {drive?.configured && drive.url ? (
            <a
              class="drive-mark"
              href={withGoogleAccount(drive.url, session.value.me?.email ?? null)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="drive-link"
              aria-label={`Open ${drive.label || label} in Google Drive`}
              title={drive.label || label}
            />
          ) : (
            <span
              class="drive-mark not-configured"
              data-testid="drive-not-configured"
              role="img"
              aria-label={`${label} — not set up yet`}
              title={drive?.reason ?? 'loading…'}
            />
          )}
          {/* Gmail, immediately beside the Drive mark (owner, 2026-08-16: "a
              gmail logo next to it they can also click which opens a gemail
              folder"). One constant URL for everyone — unlike Drive, this is
              not per-person, so it is not in driveShortcut, not fetched, and
              not a database column. Same size and hover treatment as the
              Drive mark (shared CSS below). */}
          <a
            class="gmail-mark"
            href={gmailUrlFor(email)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gmail-link"
            aria-label={mailLabel ? `Open Gmail, ${mailLabel} unread` : 'Open Gmail'}
            title="Gmail"
          >
            {mailLabel ? <span class="nav-unread" data-testid="gmail-unread">{mailLabel}</span> : null}
          </a>
          <a
            class="chat-mark"
            href={chatUrlFor(email)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="chat-link"
            aria-label={chatLabel ? `Open Chat, ${chatLabel} unread` : 'Open Chat'}
            title="Chat"
          >
            {chatLabel ? <span class="nav-unread" data-testid="chat-unread">{chatLabel}</span> : null}
          </a>
          {/* Google Calendar, after Chat (owner, 2026-08-17: after Gmail,
              before the idea bulb). Same size, same hover, same authuser
              discipline as Gmail — this opens the SIGNED-IN person's calendar,
              not the browser's default account. */}
          <a
            class="calendar-mark"
            href={calendarUrlFor(session.value.me?.email ?? null)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calendar-link"
            aria-label="Open Google Calendar"
            title="Calendar"
          />
          {/* Google Keep, after the Calendar mark (owner, 2026-08-17: "maybe
              next to email?"). Same size, same hover, same authuser
              discipline. */}
          {/* An in-app route, so no target/rel: opening our own page in a new
              tab would lose the signed-in state the SPA holds in memory. */}
          <a
            class="keep-mark"
            href={keepUrlFor()}
            onClick={(ev: Event) => { ev.preventDefault(); navigate('/ideas'); }}
            data-testid="keep-link"
            aria-label="Ideas to automate / bugs"
            title="Ideas to automate / bugs"
          />
          {/* Rackspace Email, immediately after the ideas bulb. Same 20px box
              and 22px hover as the marks before it — a nav icon, not a brand
              banner. Inline SVG so the row never fetches a third-party image. */}
          <a
            class="rackspace-mark"
            href={rackspaceUrlFor()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rackspace-link"
            aria-label="Open Rackspace Email"
            title="Rackspace Email"
          />
        </>
      )}
    </nav>
  );
}
